import React, { useEffect, useState } from "react";
import { Modal, Input, Select, Button, StatusBadge, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { PRIORITIES } from "./constants";
import { Field } from "../onboarding/_ui";

export function LeadForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    requested_service: "", source: "manual", priority: "medium", estimated_value: "",
  });
  const [dups, setDups] = useState<Record<string, unknown>[]>([]);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Live duplicate detection (never blocks; just warns).
  useEffect(() => {
    if (!form.phone && !form.email) { setDups([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.crmCheckDuplicate({ phone: form.phone, email: form.email }); setDups(r.possible_duplicates); }
      catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.phone, form.email]);

  const save = async () => {
    if (form.first_name.trim().length < 1) { toast.error("First name is required."); return; }
    setSaving(true);
    try {
      await api.crmLeadCreate({
        new_contact: {
          first_name: form.first_name.trim(), last_name: form.last_name.trim() || null,
          phone: form.phone.trim() || null, email: form.email.trim() || null,
        },
        requested_service: form.requested_service.trim() || null, source: form.source,
        priority: form.priority, estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
      });
      toast.success("Lead created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="New lead" description="Create a lead and its contact." testid="lead-form">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" required testid="field-lead-fn"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="lead-first-name" /></Field>
          <Field label="Last name" testid="field-lead-ln"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="lead-last-name" /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Phone" testid="field-lead-phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(312) 555-0123" data-testid="lead-phone" /></Field>
          <Field label="Email" testid="field-lead-email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" /></Field>
        </div>
        {dups.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="lead-dup-warning">
            <p className="text-sm text-amber-800">
              {dups.length} possible duplicate contact{dups.length > 1 ? "s" : ""} found (matching phone/email). We won't merge automatically — review before saving.
            </p>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Requested service" testid="field-lead-service"><Input value={form.requested_service} onChange={(e) => set("requested_service", e.target.value)} placeholder="AC repair" data-testid="lead-service" /></Field>
          <Field label="Source" testid="field-lead-source"><Input value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="lead-source" /></Field>
          <Field label="Priority" testid="field-lead-priority"><Select value={form.priority} onChange={(v) => set("priority", v)} options={PRIORITIES} testid="lead-priority" /></Field>
        </div>
        <Field label="Estimated value ($)" testid="field-lead-value"><Input type="number" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="lead-value" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="lead-save">{saving ? "Saving…" : "Create lead"}</Button>
        </div>
      </div>
    </Modal>
  );
}
